import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaSearch,
  FaClock,
  FaUser,
  FaCar,
  FaStickyNote,
  FaFilter,
} from "react-icons/fa";
import { API_URL } from "../../config/apiConfig";

const STATUS_COLORS = {
  open:     { bg: "bg-red-100",    text: "text-red-700",    label: "Open" },
  reviewed: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Reviewed" },
  resolved: { bg: "bg-green-100",  text: "text-green-700",  label: "Resolved" },
};

const REPORTER_COLORS = {
  driver:    { bg: "bg-blue-100",   text: "text-blue-700" },
  passenger: { bg: "bg-purple-100", text: "text-purple-700" },
  admin:     { bg: "bg-gray-100",   text: "text-gray-700" },
};

export default function RideIssues() {
  const { user } = useSelector((s) => s.auth || {});

  const [issues, setIssues]         = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch]         = useState("");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [adminNote, setAdminNote]   = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const authHeaders = user?.token
    ? { headers: { Authorization: `Bearer ${user.token}` } }
    : {};

  // ── Fetch all issues ────────────────────────────────────────────────────────
  const fetchIssues = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API_URL}/ride-issues${params}`, authHeaders);
      if (res.data?.success) setIssues(res.data.data);
    } catch (e) {
      toast.error("Failed to load ride issues");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  // ── Update issue status / admin note ────────────────────────────────────────
  const handleUpdateIssue = async (issueId, status) => {
    setSavingNote(true);
    try {
      const res = await axios.patch(
        `${API_URL}/ride-issues/${issueId}/status`,
        { status, adminNote },
        authHeaders
      );
      if (res.data?.success) {
        toast.success("Issue updated!");
        setSelectedIssue(null);
        setAdminNote("");
        fetchIssues();
      }
    } catch (e) {
      toast.error("Failed to update issue");
    } finally {
      setSavingNote(false);
    }
  };

  // ── Filter ───────────────────────────────────────────────────────────────────
  const filtered = issues.filter((issue) => {
    const q = search.toLowerCase();
    return (
      issue.rideId?.toLowerCase().includes(q) ||
      issue.readableBookingId?.toLowerCase().includes(q) ||
      issue.issue?.toLowerCase().includes(q) ||
      issue.reporterName?.toLowerCase().includes(q) ||
      issue.reporterId?.toLowerCase().includes(q)
    );
  });

  const counts = {
    all:      issues.length,
    open:     issues.filter((i) => i.status === "open").length,
    reviewed: issues.filter((i) => i.status === "reviewed").length,
    resolved: issues.filter((i) => i.status === "resolved").length,
  };

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex justify-end mb-6">
        <button
          onClick={fetchIssues}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { key: "all",      label: "All Issues",  color: "border-gray-300 bg-gray-50" },
          { key: "open",     label: "Open",        color: "border-red-300 bg-red-50" },
          { key: "reviewed", label: "Reviewed",    color: "border-yellow-300 bg-yellow-50" },
          { key: "resolved", label: "Resolved",    color: "border-green-300 bg-green-50" },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`border-2 rounded-xl p-4 text-left transition hover:shadow ${color} ${
              statusFilter === key ? "ring-2 ring-blue-400" : ""
            }`}
          >
            <div className="text-2xl font-bold text-gray-900">{counts[key]}</div>
            <div className="text-sm text-gray-600">{label}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by Ride ID, issue text, reporter name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-10 h-10 border-4 border-blue-300 border-t-blue-600 rounded-full" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FaExclamationTriangle className="mx-auto text-4xl mb-3 text-gray-300" />
          <p className="text-lg">No issues found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                {["Ride ID", "Reported By", "Issue", "Status", "Time", "Actions"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((issue) => {
                const sc = STATUS_COLORS[issue.status] || STATUS_COLORS.open;
                const rc = REPORTER_COLORS[issue.reportedBy] || REPORTER_COLORS.passenger;
                return (
                  <tr key={issue._id} className="hover:bg-gray-50 transition">
                    {/* Ride ID */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-semibold text-blue-600">
                        {issue.readableBookingId ? `#${issue.readableBookingId}` : issue.rideId}
                      </span>
                    </td>

                    {/* Reported By */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${rc.bg} ${rc.text}`}
                      >
                        {issue.reportedBy === "driver" ? <FaCar size={10} /> : <FaUser size={10} />}
                        {issue.reportedBy}
                      </span>
                      {issue.reporterName && (
                        <div className="text-xs text-gray-500 mt-0.5">{issue.reporterName}</div>
                      )}
                    </td>

                    {/* Issue text */}
                    <td className="px-4 py-3 max-w-xs">
                      {issue.issue ? (
                        <p className="text-sm text-gray-700 line-clamp-2">{issue.issue}</p>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No description</span>
                      )}
                      {issue.adminNote && (
                        <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                          <FaStickyNote size={10} />
                          {issue.adminNote}
                        </p>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${sc.bg} ${sc.text}`}
                      >
                        {issue.status === "resolved"
                          ? <FaCheckCircle size={10} />
                          : <FaClock size={10} />}
                        {sc.label}
                      </span>
                    </td>

                    {/* Time */}
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(issue.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                      <br />
                      {new Date(issue.createdAt).toLocaleTimeString("en-IN", {
                        hour: "2-digit", minute: "2-digit", hour12: true,
                      })}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setSelectedIssue(issue);
                          setAdminNote(issue.adminNote || "");
                        }}
                        className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg font-medium transition"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Review Modal */}
      {selectedIssue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="text-red-400" />
                  Ride Issue
                </h2>
                <span className="text-sm font-mono text-blue-600">
                  {selectedIssue.readableBookingId ? `#${selectedIssue.readableBookingId}` : `#${selectedIssue.rideId}`}
                </span>
              </div>
              <button
                onClick={() => setSelectedIssue(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            {/* Issue details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-600 w-28">Reported by:</span>
                <span className="capitalize font-medium">
                  {selectedIssue.reportedBy}
                  {selectedIssue.reporterName && ` — ${selectedIssue.reporterName}`}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <span className="font-semibold text-gray-600 w-28 shrink-0">Issue:</span>
                <span className="text-gray-700">
                  {selectedIssue.issue || <em className="text-gray-400">No description provided</em>}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-600 w-28">Reported at:</span>
                <span className="text-gray-700">
                  {new Date(selectedIssue.createdAt).toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            {/* Admin note */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Admin Note (optional)
              </label>
              <textarea
                rows={3}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add a note about this issue..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                disabled={savingNote}
                onClick={() => handleUpdateIssue(selectedIssue._id, "reviewed")}
                className="flex-1 bg-yellow-500 text-white py-2 rounded-lg text-sm font-semibold hover:bg-yellow-600 transition disabled:opacity-60"
              >
                Mark as Reviewed
              </button>
              <button
                disabled={savingNote}
                onClick={() => handleUpdateIssue(selectedIssue._id, "resolved")}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition disabled:opacity-60"
              >
                ✓ Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
